<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
import { useGraphStore } from '@/modules/dashboard/stores/graphStore'
import { useFilterPresets } from '@/modules/dashboard/composables/useFilterPresets'

// Импорт компонентов
import DashboardFilterSidebar from '@/modules/dashboard/components/DashboardFilterSidebar.vue'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import ActivityByDaysWidget from '@/modules/dashboard/components/ActivityByDaysWidget.vue'
import ActivityByModulesWidget from '@/modules/dashboard/components/ActivityByModulesWidget.vue'
import ListWidget from '@/modules/dashboard/components/ListWidget.vue'
import MultipleCountWidget from '@/modules/dashboard/components/MultipleCountWidget.vue'
import PieChartInfoWidget from '@/modules/dashboard/components/PieChartInfoWidget.vue'
import TreemapWidget from '@/modules/dashboard/components/TreemapWidget.vue'
import ChartJsLineChart from '@/views/charts/chartjs/ChartJsLineChart.vue'
import ChartJsBarChart from '@/views/charts/chartjs/ChartJsBarChart.vue'
import ChartJsHorizontalBarChart from '@/views/charts/chartjs/ChartJsHorizontalBarChart.vue'

// Импорт типов для каста
import type { IGraphTarget } from '@/modules/dashboard/types/graph/IGraphTarget'
import type { ITableWidget } from '@/modules/dashboard/types/graph/ITableWidget'
import type { IActivityByDaysWidget } from '@/modules/dashboard/types/IActivityByDaysWidget'
import type { IActivityByModulesWidget } from '@/modules/dashboard/types/IActivityByModulesWidget'
import type { IBarChartWidget } from '@/modules/dashboard/types/IBarChartWidget'
import type { ILineChartWidget } from '@/modules/dashboard/types/ILineChartWidget'
import type { IMultipleCountWidget } from '@/modules/dashboard/types/IMultipleCountWidget'
import type { IPieChartInfoWidget } from '@/modules/dashboard/types/IPieChartInfoWidget'
import type { ITreemapWidget } from '@/modules/dashboard/types/ITreemapWidget'
import type { IWidget } from '@/modules/dashboard/types/IWidget'
import type { IWidgetBase } from '@/modules/dashboard/types/IWidgetBase'

// Props компонента
interface Props {
  target: IGraphTarget // Обязательный параметр - что отображаем
  initialFilters?: Record<string, any> // Опциональные начальные фильтры
  autoFetch?: boolean // Автоматически загружать при монтировании (по умолчанию true)
  /** Вкладка ситуационного центра — фильтры из URL читаются только если query.tab совпадает */
  dashboardTab?: string
}

const props = withDefaults(defineProps<Props>(), {
  initialFilters: () => ({}),
  autoFetch: true,
})

const graphStore = useGraphStore()
const display = useDisplay()
const { addPreset } = useFilterPresets()

const isSavePresetDialogOpen = ref(false)
const newPresetName = ref('')
const presetNameError = ref('')

const openSavePresetDialog = () => {
  newPresetName.value = ''
  presetNameError.value = ''
  isSavePresetDialogOpen.value = true
}

const handleSavePreset = () => {
  const trimmed = newPresetName.value.trim()
  if (!trimmed) {
    presetNameError.value = 'Введите название фильтра'
    return
  }

  addPreset(
    props.target,
    trimmed,
    graphStore.activeFilters,
    graphStore.groupBy,
    graphStore.dateTrunc,
  )

  isSavePresetDialogOpen.value = false
}

const STORAGE_POSITION_KEY = 'materio.pane-position.dashboard-filters'

const readStoredSidebarPosition = (): 'left' | 'right' => {
  try {
    const raw = localStorage.getItem(STORAGE_POSITION_KEY)
    if (raw === 'left' || raw === 'right') return raw
  } catch {}
  return 'right'
}

const sidebarPosition = ref<'left' | 'right'>(readStoredSidebarPosition())
const isMobileFilterDrawerOpen = ref(false)

const activeFiltersCount = computed(() => graphStore.activeFilterChips.length)

let route: any = null
let router: any = null
try {
  route = useRoute()
  router = useRouter()
} catch (e) {
  // Игнорируем вне контекста vue-router
}

const hasWidgets = computed(() => graphStore.widgets.length > 0)

const isFullWidthChartType = (widgetType?: string): boolean => {
  if (!widgetType) return false
  return ['activity_by_days', 'line_chart', 'activity_by_modules'].includes(widgetType)
}

/*
 * Сколько колонок 12-колоночной сетки должен занять виджет.
 * - < 960px (smAndDown): всегда вся строка (12).
 * - Широкие графики временных рядов (activity_by_days, line_chart, activity_by_modules):
 *   на экранах < 1920px (или если нет явной ширины < 6) — всегда 12 (на всю строку),
 *   на сверхшироких экранах (xl+) — согласно widget.width (но не менее 6).
 * - Информационные карточки (table, pie_chart_info, multiple_count):
 *   - 960px - 1440px: если widget.width <= 4 -> 4 (по 3 в строке, 4+4+4=12),
 *     если widget.width 5-8 -> 6 (по 2 в строке), иначе 12.
 *   - > 1440px: если backend передал widget.width > 0 -> min 3, max 12 (нормализуем 2 -> 3),
 *     по умолчанию 4 (по 3 в строке).
 */
const getWidgetSpan = (widget: IWidget | IWidgetBase): number => {
  if (display.smAndDown.value) return 12
  if (widget.widget_type === 'treemap') return 12

  if (isFullWidthChartType(widget.widget_type)) {
    if (!display.xlAndUp.value) return 12
    return widget.width && widget.width >= 6 ? Math.min(12, widget.width) : 12
  }

  if (display.width.value < 1440) {
    if (!widget.width || widget.width <= 4) return 4
    if (widget.width <= 8) return 6
    return 12
  }

  if (widget.width && widget.width > 0) {
    if (widget.width === 2) return 3
    return Math.min(12, widget.width)
  }

  return 4
}

// Inline-стиль для grid-column конкретного виджета.
const getWidgetStyle = (widget: IWidget | IWidgetBase) => ({
  gridColumn: `span ${getWidgetSpan(widget)}`,
})

// Метод для загрузки данных (можно вызывать из родителя)
const fetchData = async (additionalFilters: Record<string, any> = {}) => {
  const filters = { ...props.initialFilters, ...additionalFilters }
  await graphStore.fetchGraph(props.target, filters)
}

const GRAPH_QUERY_RESERVED_KEYS = new Set(['group_by', 'combine', 'date_trunc', 'tab'])

/**
 * Инициализирует состояние фильтров, группировки и режимов комбинирования из URL query-параметров.
 * Сначала считывает параметры во временные переменные, затем полностью сбрасывает стор
 * и записывает новые значения, чтобы избежать промежуточных срабатываний ватчера URL.
 */
const initFromUrl = () => {
  let groupBy: string | null = null
  let dateTrunc: string | null = null
  const urlFilters: Record<string, any> = {}

  const urlTab = route?.query?.tab ? String(route.query.tab) : null
  const hasTabParam = Boolean(route?.query && 'tab' in route.query)
  const shouldLoadFilters = !props.dashboardTab
    || urlTab === props.dashboardTab
    || (urlTab === null && !hasTabParam)

  if (route && route.query && shouldLoadFilters) {
    if (route.query.group_by) {
      groupBy = String(route.query.group_by)
    }
    if (route.query.date_trunc) {
      dateTrunc = String(route.query.date_trunc)
    }

    for (const [key, val] of Object.entries(route.query)) {
      if (GRAPH_QUERY_RESERVED_KEYS.has(key)) continue
      if (!val) continue

      const strVal = String(val)
      if (strVal.includes(',')) {
        urlFilters[key] = strVal.split(',').map(id => ({ id, label: '' }))
      } else {
        urlFilters[key] = { id: strVal, label: '' }
      }
    }
  }

  // По умолчанию включаем группировку для первого фильтра, где выбрано 2+ значений (если явно не задано)
  if (!groupBy && shouldLoadFilters) {
    for (const [key, val] of Object.entries(urlFilters)) {
      if (Array.isArray(val) && val.length >= 2) {
        groupBy = key
        break
      }
    }
  }

  graphStore.reset()

  graphStore.groupBy = groupBy
  graphStore.dateTrunc = dateTrunc
  if (Object.keys(urlFilters).length > 0) {
    graphStore.activeFilters = urlFilters
    // Догружаем варианты для фильтров из URL, чтобы чипы сразу отображали понятные названия
    Object.keys(urlFilters).forEach(key => {
      if (!graphStore.filterChoices[key]?.length && !graphStore.filterLoading[key]) {
        graphStore.fetchFilterChoices(key, '', undefined, undefined, props.target)
      }
    })
  }
}

// Сброс и повторная инициализация из URL при смене целевого графика
watch(() => props.target, () => {
  initFromUrl()
  if (props.autoFetch) {
    fetchData()
  }
})

// Автоматическая загрузка при монтировании
onMounted(async () => {
  initFromUrl()
  if (props.autoFetch) {
    await fetchData()
  }
})

// Синхронизация изменений фильтров и группировки из стора обратно в URL query-параметры
if (route && router) {
  watch(
    [
      () => graphStore.activeFilters,
      () => graphStore.groupBy,
      () => graphStore.dateTrunc,
    ],
    ([newFilters, newGroupBy, newDateTrunc]) => {
      if (
        props.dashboardTab
        && graphStore.querySyncTab
        && graphStore.querySyncTab !== props.dashboardTab
      ) {
        return
      }

      const query: Record<string, any> = {}

      if (newGroupBy) {
        query.group_by = newGroupBy
      }

      if (newDateTrunc) {
        query.date_trunc = newDateTrunc
      }

      for (const [key, value] of Object.entries(newFilters)) {
        if (!value) continue
        if (Array.isArray(value)) {
          if (value.length > 0) {
            query[key] = value.map(item => item.id).join(',')
          }
        } else if (typeof value === 'object' && 'id' in value) {
          query[key] = String((value as any).id)
        }
      }

      if (props.dashboardTab) {
        query.tab = props.dashboardTab
      }

      if (route.query) {
        if (!props.dashboardTab && route.query.tab) {
          query.tab = route.query.tab
        }

        /* На отдельных страницах графика сохраняем прочие query-параметры;
           на вкладках сит.центра — только то, что относится к текущей вкладке. */
        if (!props.dashboardTab) {
          for (const [key, val] of Object.entries(route.query)) {
            if (
              !(key in query)
              && !GRAPH_QUERY_RESERVED_KEYS.has(key)
              && !graphStore.widgets.some((w: any) => w.data?.key === key)
            ) {
              query[key] = val
            }
          }
        }
      }

      router.replace({ query }).catch((err: any) => {
        if (err.name !== 'NavigationDuplicated') {
          console.error(err)
        }
      })
    },
    { deep: true },
  )
}

/*
 * Сброс накопленных фильтров графика при уходе со страницы (без запроса),
 * чтобы возврат на /adverse-graph (и аналогичные) давал чистое состояние.
 */
onUnmounted(() => {
  graphStore.reset()
})

const widgetsWithData = computed(() => {
  return graphStore.widgets.filter((widget: any) => {
    if (!widget?.data) return false

    switch (widget.widget_type) {
      case 'multiple_count':
        return Array.isArray(widget.data) && widget.data.some((item: any) => item.value > 0)

      case 'pie_chart_info':
        return widget.data?.datasets?.[0]?.data?.length > 0

      case 'treemap':
        return widget.data?.series?.[0]?.data?.some((point: { y?: number }) => Number(point?.y) > 0)

      case 'table':
        return widget.data?.datasets?.[0]?.data?.length > 0

      case 'line_chart':
      case 'vertical_bar_chart':
      case 'horizontal_bar_chart':
        return widget.data?.datasets?.length > 0

      case 'activity_by_days':
        return widget.data?.items?.length > 0

      case 'activity_by_modules':
        return Array.isArray(widget.data) && widget.data.length > 0

      default:
        return true
    }
  })
})
</script>

<template>
  <div class="universal-graph d-flex flex-column ga-4">
    <!-- Плавающая мобильная кнопка фильтров (позиционируется под хедером под бургер-меню) -->
    <Transition name="fab-scale">
      <VBtn
        v-if="display.smAndDown.value"
        v-show="!isMobileFilterDrawerOpen"
        icon
        color="primary"
        size="small"
        elevation="4"
        class="mobile-filter-floating-btn"
        aria-label="Фильтры"
        @click="isMobileFilterDrawerOpen = true"
      >
        <VBadge
          v-if="activeFiltersCount > 0"
          :content="activeFiltersCount"
          color="error"
          offset-x="-2"
          offset-y="-2"
        >
          <VIcon icon="ri-filter-3-line" size="20" />
        </VBadge>
        <VIcon v-else icon="ri-filter-3-line" size="20" />
      </VBtn>
    </Transition>

    <!-- Панель активных чипов фильтров и кнопок действий -->
    <div
      v-if="graphStore.activeFilterChips.length > 0"
      class="filter-chips-bar"
    >
      <div class="filter-chips-wrapper d-flex align-center gap-2 flex-wrap">
        <!-- Список чипов фильтров -->
        <div class="filter-chips-list d-flex align-center gap-2 flex-wrap">
          <VChip
            v-for="chip in graphStore.activeFilterChips"
            :key="`${chip.key}-${chip.id}`"
            closable
            size="small"
            @click:close="graphStore.removeGraphFilter(target, chip.key, chip.id)"
          >
            {{ chip.label }}
          </VChip>
        </div>

        <!-- Кнопки сохранения и сброса фильтров: на мобиле под чипами во всю ширину, на ПК справа -->
        <div
          v-if="graphStore.hasActiveFilters"
          class="filter-actions-wrapper d-flex align-center gap-2"
          :class="display.smAndDown.value ? 'w-100 mt-3 flex-column' : 'ms-auto'"
        >
          <VBtn
            prepend-icon="ri-save-line"
            variant="outlined"
            color="secondary"
            class="text-color-secondary"
            :class="display.smAndDown.value ? 'w-100' : ''"
            @click="openSavePresetDialog"
          >
            Сохранить фильтр
          </VBtn>

          <VBtn
            prepend-icon="ri-delete-bin-line"
            variant="outlined"
            color="secondary"
            class="text-color-secondary"
            :class="display.smAndDown.value ? 'w-100' : ''"
            :loading="graphStore.isLoading"
            @click="graphStore.resetGraphFilters(target)"
          >
            Сбросить фильтры
          </VBtn>
        </div>
      </div>
    </div>

    <!-- Основная рабочая область: Сайдбар слева/справа и виджеты на одном уровне -->
    <div
      class="dashboard-main-row d-flex flex-column flex-md-row ga-4 align-stretch"
      :class="{ 'flex-md-row-reverse': sidebarPosition === 'right' }"
    >
      <!-- Сайдбар фильтрации -->
      <DashboardFilterSidebar
        v-model:position="sidebarPosition"
        v-model:is-drawer-open="isMobileFilterDrawerOpen"
        :target="target"
        :filters="graphStore.filters"
        :loading="graphStore.isLoading"
      />

      <!-- Основная область с графиками и виджетами -->
      <div class="graph-content-area flex-grow-1 w-100 min-width-0 d-flex flex-column">
        <!-- Скелетоны при первой загрузке -->
        <div
          v-if="graphStore.isLoading && !hasWidgets"
          class="widgets-grid w-100"
        >
          <VCard
            v-for="i in 4"
            :key="i"
            class="pa-6 d-flex flex-column gap-4 widget-col"
            :style="{ gridColumn: display.mdAndDown.value ? 'span 12' : (i <= 2 ? 'span 12' : 'span 6') }"
          >
            <div class="d-flex align-center justify-space-between">
              <VSkeletonLoader type="text" width="30%" class="my-1" />
            </div>
            <div class="px-3 py-1">
              <div v-for="j in 4" :key="j" class="d-flex align-center justify-space-between py-2">
                <VSkeletonLoader type="text" width="60%" class="my-1" />
                <VSkeletonLoader type="text" width="15%" class="my-1" />
              </div>
            </div>
          </VCard>
        </div>

        <!-- Пустое состояние, когда нет виджетов или нет данных под фильтры -->
        <div
          v-else-if="(!hasWidgets || widgetsWithData.length === 0) && !graphStore.isLoading"
          class="empty-graph-state flex-grow-1 d-flex flex-column align-center justify-center pa-10 text-center"
        >
          <VIcon icon="ri-bar-chart-box-line" size="56" class="empty-state-icon mb-4" />
          <div class="empty-state-title mb-2">
            Недостаточно данных для построения виджетов
          </div>
          <div class="empty-state-subtitle">
            Попробуйте выбрать другой период или изменить параметры фильтрации
          </div>
        </div>

        <!-- Сетка виджетов при наличии данных -->
        <div v-else class="widgets-grid w-100">
          <template v-for="(widget, index) in widgetsWithData" :key="index">
            <MultipleCountWidget
              v-if="widget.widget_type === 'multiple_count'"
              :widget-data="widget as IMultipleCountWidget"
              :current-target="target"
              :loading="graphStore.isLoading"
              class="widget-col"
              :style="getWidgetStyle(widget)"
            />

            <PieChartInfoWidget
              v-else-if="widget.widget_type === 'pie_chart_info'"
              :widget-data="widget as IPieChartInfoWidget"
              :current-target="target"
              :loading="graphStore.isLoading"
              class="widget-col"
              :style="getWidgetStyle(widget)"
            />

            <TreemapWidget
              v-else-if="widget.widget_type === 'treemap'"
              :widget-data="widget as ITreemapWidget"
              :current-target="target"
              :loading="graphStore.isLoading"
              class="widget-col"
              :style="getWidgetStyle(widget)"
            />

            <ListWidget
              v-else-if="widget.widget_type === 'table'"
              :widget-data="widget as ITableWidget"
              :current-target="target"
              :loading="graphStore.isLoading"
              class="widget-col"
              :style="getWidgetStyle(widget)"
            />

            <ChartJsLineChart
              v-else-if="widget.widget_type === 'line_chart'"
              :widget-data="widget as ILineChartWidget"
              :current-target="target"
              :loading="graphStore.isLoading"
              class="widget-col"
              :style="getWidgetStyle(widget)"
            />

            <ChartJsBarChart
              v-else-if="widget.widget_type === 'vertical_bar_chart'"
              :widget-data="widget as IBarChartWidget"
              :current-target="target"
              :loading="graphStore.isLoading"
              class="widget-col"
              :style="getWidgetStyle(widget)"
            />

            <ChartJsHorizontalBarChart
              v-else-if="widget.widget_type === 'horizontal_bar_chart'"
              :widget-data="widget as IBarChartWidget"
              :current-target="target"
              class="widget-col"
              :style="getWidgetStyle(widget)"
            />

            <ActivityByDaysWidget
              v-else-if="widget.widget_type === 'activity_by_days'"
              :widget-data="widget as IActivityByDaysWidget"
              :current-target="target"
              :loading="graphStore.isLoading"
              class="widget-col"
              :style="getWidgetStyle(widget)"
            />

            <ActivityByModulesWidget
              v-else-if="widget.widget_type === 'activity_by_modules'"
              :widget-data="widget as IActivityByModulesWidget"
              :current-target="target"
              :loading="graphStore.isLoading"
              class="widget-col"
              :style="getWidgetStyle(widget)"
            />
          </template>
        </div>

        <!-- Слот для дополнительных элементов управления -->
        <slot
          name="controls"
          :fetch-data="fetchData"
          :is-loading="graphStore.isLoading"
        />
      </div>
    </div>

    <!-- Диалог сохранения пресета фильтра -->
    <VDialog
      v-model="isSavePresetDialogOpen"
      max-width="500"
    >
      <VCard class="pa-6">
        <div class="d-flex justify-space-between align-center mb-6">
          <span class="text-h4 font-weight-medium">Сохранить фильтр</span>
          <CustomCloseButton :close-action="() => isSavePresetDialogOpen = false" />
        </div>

        <VTextField
          v-model="newPresetName"
          label="Название фильтра"
          placeholder="Например, Все НС за 25-26"
          variant="outlined"
          persistent-placeholder
          autofocus
          clearable
          :error-messages="presetNameError"
          @keyup.enter="handleSavePreset"
          @input="presetNameError = ''"
        />

        <div class="d-flex justify-end mt-6">
          <VBtn
            color="primary"
            variant="flat"
            :disabled="!newPresetName.trim()"
            @click="handleSavePreset"
          >
            Сохранить
          </VBtn>
        </div>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped lang="scss">
.universal-graph {
  width: 100%;
}

.filter-chip {
  overflow-x: auto;
  white-space: nowrap;

  :deep(.v-chip) {
    flex-shrink: 0;
    max-width: none !important;
    white-space: nowrap;
    overflow: visible;
  }
}

.dashboard-main-row {
  min-height: 520px;

  @media (min-width: 961px) {
    min-height: calc(100vh - 160px);
  }
}

.graph-content-area {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.empty-graph-state {
  min-height: 360px;
  width: 100%;
  border-radius: 8px;
  border: 1px dashed rgba(var(--v-theme-on-surface), var(--v-custom-opacity, 0.12));
  background-color: rgba(var(--v-theme-surface), 0.35);
}

.empty-state-icon {
  color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity, 0.38));
}

.empty-state-title {
  font-size: 16px;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity, 0.87));
}

.empty-state-subtitle {
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity, 0.6));
}

/*
 * 12-колоночная сетка для виджетов. Ширина каждого потомка задаётся
 * inline-стилем grid-column: span N (см. getWidgetStyle в скрипте),
 * где N — widget.width из ответа бэкенда (или 12 на узких экранах,
 * или 6 при отсутствии поля).
 * minmax(0, 1fr) не даёт длинному контенту раздвигать колонки.
 */
.widgets-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 16px;
}

.mobile-filter-floating-btn {
  position: fixed;
  top: 142px;
  left: 16px;
  z-index: 99;
  border-radius: 50% !important;
}

@media (max-width: 960px) {
  .filter-chips-list {
    padding-left: 44px;
    min-height: 40px;
  }
}

.fab-scale-enter-active,
.fab-scale-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fab-scale-enter-from,
.fab-scale-leave-to {
  opacity: 0;
  transform: scale(0.6);
}
</style>

